import {
  INTERACTIVE_LAYER_METADATA,
  isTopmostInteractiveLayer,
} from "@/features/map/utils/maplibreClickPriority";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import * as Sentry from "@sentry/react";
import "../../styles/layer.css";
import { useMaplibreContext } from "@/features/map/components/mapLibre/context/MaplibreLayerContext";
import { isMaplibreMapAlive } from "@/features/map/utils/leafletMaplibreInternals";
import { LatLng, Popup } from "leaflet";
import { type MapLibreClickHandler } from "@/features/map/types/mapLibre";
import { registerPrintLayerAdapter } from "@/features/map/utils/printLayerAdapterRegistry";
import { createMaplibrePrintAdapter } from "@/features/map/utils/maplibrePrintAdapter";

interface Props {
  visible: boolean;
  initialZoomLevel: number;
  tileHost: string | undefined;
  /** Leaflet の attribution コントロールに出す出典文字列 */
  attribution?: string;
}

const typeTextTable: Record<string, string> = {
  "1": "第一種低層住居専用",
  "2": "第二種低層住居専用",
  "3": "第一種中高層住居専用",
  "4": "第二種中高層住居専用",
  "5": "第一種住居",
  "6": "第二種住居",
  "7": "準住居",
  "8": "近隣商業",
  "9": "商業",
  "10": "準工業",
  "11": "工業",
  "12": "工業専用",
  "13": "田園住居地域",
};

export const AREA_USE_PURPOSE_MIN_ZOOM = 14;
export const AREA_USE_PURPOSE_MAX_ZOOM = 22;
const MIN_ZOOM = AREA_USE_PURPOSE_MIN_ZOOM;
const MAX_ZOOM = AREA_USE_PURPOSE_MAX_ZOOM;
const SOURCE_NAME = "areaUsePurpose";
const SOURCE_LAYER_NAME = "areausepurpose";
export const NATIONAL_LAND_NUMERICAL_INFORMATION_HOST =
  process.env
    .VITE_AREA_USE_PURPOSE_TILE_NATIONAL_LAND_NUMERICAL_INFORMATION_HOST;
export const CITY_PLANNING_DECISION_GIS_DATA_HOST =
  process.env.VITE_AREA_USE_PURPOSE_TILE_CITY_PLANNING_DECISION_GIS_DATA_HOST;
export const CITY_PLANNING_DECISION_GIS_DATA_YEAR =
  process.env.VITE_AREA_USE_PURPOSE_TILE_CITY_PLANNING_DECISION_GIS_DATA_YEAR;

const getTileVersion = (host: string): string => {
  // 都市計画決定GISデータの場合は環境変数から取得（デフォルト: "2023"）
  if (host === CITY_PLANNING_DECISION_GIS_DATA_HOST) {
    return CITY_PLANNING_DECISION_GIS_DATA_YEAR ?? "2023";
  }

  // 国土数値情報の場合は固定値
  return "v3";
};

// tileHost に基づいて uniqu なソース ID suffix を生成
const getSourceSuffix = (host?: string): string => {
  if (host === NATIONAL_LAND_NUMERICAL_INFORMATION_HOST) return "national";
  if (host === CITY_PLANNING_DECISION_GIS_DATA_HOST) return "city";
  return "custom";
};

/*
用途地域レイヤーのコンポーネント
MaplibreLayerContext が管理する単一の MapLibre インスタンスに対して
ソースとレイヤーを追加・削除する
*/
export const AreaUsePurposeTileLayer: React.FC<Props> = ({
  visible,
  initialZoomLevel,
  tileHost,
  attribution,
}) => {
  // Leaflet マップオブジェクト
  const leafletMap = useMap();
  // MaplibreLayerContext から提供される Context
  const { maplibreMap } = useMaplibreContext();
  // ポップアップ格納用
  const popupRef = useRef<Popup | null>(null);
  // 選択中のID格納用
  const selectedIdRef = useRef<string | number | undefined>(undefined);
  // ズームレベル
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoomLevel);
  // ソース ID に unique suffix をつける
  const sourceSuffix = getSourceSuffix(tileHost);
  const uniqueSourceName = `${SOURCE_NAME}_${sourceSuffix}`;
  const uniqueLayerIdPrefix = `${sourceSuffix}_`;

  // レイヤー ID（独自のプレフィックス付き）
  const polygonLayerId = `${uniqueLayerIdPrefix}type_polygon`;
  const lineLayerId = `${uniqueLayerIdPrefix}type_line`;
  const symbolLayerId = `${uniqueLayerIdPrefix}type_symbol`;

  useEffect(() => {
    if (!maplibreMap || !tileHost) return;

    return registerPrintLayerAdapter(leafletMap, () =>
      createMaplibrePrintAdapter({
        map: maplibreMap,
        layers: [
          {
            id: lineLayerId,
            visible,
            paintPixelProperties: [
              "line-width",
              "line-gap-width",
              "line-offset",
              "line-blur",
            ],
          },
          {
            id: symbolLayerId,
            visible,
            layoutPixelProperties: [
              "text-size",
              "text-padding",
              "icon-size",
              "icon-padding",
            ],
            paintPixelProperties: [
              "text-halo-width",
              "text-halo-blur",
              "icon-halo-width",
              "icon-halo-blur",
            ],
          },
        ],
      })
    );
  }, [leafletMap, maplibreMap, tileHost, lineLayerId, symbolLayerId, visible]);

  // クリックイベントハンドラ
  const clickHandler = useCallback<MapLibreClickHandler>(
    (e) => {
      if (
        !maplibreMap ||
        !isTopmostInteractiveLayer(maplibreMap, e.point, symbolLayerId)
      )
        return;
      e.originalEvent.stopPropagation();
      if (!maplibreMap) return;

      const features = e.features;
      if (features && features.length > 0) {
        const feature = features[0];

        // feature.properties は MapLibre の型定義上 null/undefined にはならない想定だが
        // 万が一null/undefinedの場合はfeaturesが無い場合と同様になにもしない
        const properties = feature.properties;
        if (!properties) return;

        const typeText = typeTextTable[properties.t as string] ?? "不明";
        const buildingRate = properties.br as number;
        const volumeRate = properties.vr as number;

        const description = `${typeText}<br>建ぺい率: ${buildingRate}%<br>容積率: ${volumeRate}%`;
        const coordinates = new LatLng(e.lngLat.lat, e.lngLat.lng);

        if (selectedIdRef.current && popupRef.current) {
          popupRef.current.remove();
        }

        popupRef.current = new Popup({
          offset: [0, -20],
          content: description,
          closeButton: false,
          closeOnClick: false,
        })
          .setLatLng(coordinates)
          .openOn(leafletMap);

        const aid = feature.id;
        if (selectedIdRef.current) {
          maplibreMap.setFeatureState(
            {
              source: uniqueSourceName,
              sourceLayer: SOURCE_LAYER_NAME,
              id: selectedIdRef.current,
            },
            {
              selected: false,
            }
          );
        }

        maplibreMap.setFeatureState(
          {
            source: uniqueSourceName,
            sourceLayer: SOURCE_LAYER_NAME,
            id: aid,
          },
          {
            selected: true,
          }
        );
        selectedIdRef.current = aid;
      }
    },
    [maplibreMap, uniqueSourceName, symbolLayerId]
  );

  // ソースとレイヤーを追加
  const setupLayers = useCallback((): void => {
    if (!maplibreMap || !tileHost) return;

    const tileVersion = getTileVersion(tileHost);

    // Leaflet の attribution コントロールに出典を追加
    if (attribution) {
      leafletMap.attributionControl.addAttribution(attribution);
    }

    // ソースがまだ追加されていない場合は追加
    if (!maplibreMap.getSource(uniqueSourceName)) {
      maplibreMap.addSource(uniqueSourceName, {
        type: "vector",
        tiles: [`${tileHost}/tiles/${tileVersion}/{z}/{x}/{y}.pbf`],
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
      });
    }

    // ポリゴンレイヤー
    if (!maplibreMap.getLayer(polygonLayerId)) {
      maplibreMap.addLayer({
        id: polygonLayerId,
        type: "fill",
        source: uniqueSourceName,
        "source-layer": SOURCE_LAYER_NAME,
        paint: {
          "fill-color": [
            "match",
            ["get", "t"],
            "1",
            "#00FF99",
            "2",
            "#00552E",
            "3",
            "#92D050",
            "4",
            "#CCFF99",
            "5",
            "#FFFF66",
            "6",
            "#FFCC99",
            "7",
            "#FFCC66",
            "8",
            "#FF66CC",
            "9",
            "#FF0066",
            "10",
            "#9900FF",
            "11",
            "#CCFFFF",
            "12",
            "#99CCFF",
            "13",
            "#B8860B",
            "#666666",
          ],
          "fill-outline-color": "#000000",
          "fill-opacity": 0.3,
        },
        layout: {
          visibility: "none",
        },
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
      });
    }

    // ハイライト用ラインレイヤー
    if (!maplibreMap.getLayer(lineLayerId)) {
      maplibreMap.addLayer({
        id: lineLayerId,
        type: "line",
        source: uniqueSourceName,
        "source-layer": SOURCE_LAYER_NAME,
        paint: {
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            1,
            0,
          ],
          "line-color": "#FF0000",
          "line-width": 3,
          "line-gap-width": 0,
          "line-offset": 0,
          "line-blur": 0,
        },
        layout: {
          visibility: "none",
        },
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
      });
    }

    // シンボルレイヤー（ラベル）
    if (!maplibreMap.getLayer(symbolLayerId)) {
      maplibreMap.addLayer({
        id: symbolLayerId,
        metadata: INTERACTIVE_LAYER_METADATA,
        type: "symbol",
        source: uniqueSourceName,
        "source-layer": SOURCE_LAYER_NAME,
        layout: {
          "text-field": [
            "match",
            ["get", "t"],
            "1",
            "第一種低層住居専用",
            "2",
            "第二種低層住居専用",
            "3",
            "第一種中高層住居専用",
            "4",
            "第二種中高層住居専用",
            "5",
            "第一種住居",
            "6",
            "第二種住居",
            "7",
            "準住居",
            "8",
            "近隣商業",
            "9",
            "商業",
            "10",
            "準工業",
            "11",
            "工業",
            "12",
            "工業専用",
            "不明",
            "13",
            "田園住居地域",
          ],
          "text-font": ["Open Sans Regular"],
          "text-offset": [0, 1.25],
          "text-anchor": "top",
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          "text-rotation-alignment": "map",
          "text-size": 14,
          "text-padding": 8,
          "icon-size": 1,
          "icon-padding": [2],
          visibility: "none",
        },
        paint: {
          "text-color": "#0c0063",
          "text-halo-color": "#FFFFFF",
          "text-halo-width": 3,
          "text-halo-blur": 0,
          "icon-halo-width": 0,
          "icon-halo-blur": 0,
        },
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
      });
    }
  }, [
    maplibreMap,
    tileHost,
    uniqueSourceName,
    polygonLayerId,
    lineLayerId,
    symbolLayerId,
    attribution,
    leafletMap,
  ]);

  // レイヤー初期化を行う
  useEffect(() => {
    if (!maplibreMap || !tileHost) return;

    const handleLoad = (): void => {
      setupLayers();
    };

    if (maplibreMap.isStyleLoaded()) {
      setupLayers();
    } else {
      maplibreMap.on("load", handleLoad);
    }

    return () => {
      maplibreMap.off("load", handleLoad);

      // 画面遷移で地図ごと破棄された場合、maplibre は内部 style を失っており
      // getLayer 等を呼ぶと例外になる。地図破棄時は個別削除も不要なのでスキップする。
      if (isMaplibreMapAlive(maplibreMap)) {
        // レイヤーを先に削除してからソースを削除（MapLibreの制約）
        [polygonLayerId, lineLayerId, symbolLayerId].forEach((layerId) => {
          if (maplibreMap.getLayer(layerId)) {
            maplibreMap.removeLayer(layerId);
          }
        });

        if (maplibreMap.getSource(uniqueSourceName)) {
          maplibreMap.removeSource(uniqueSourceName);
        }
      }

      if (attribution) {
        leafletMap.attributionControl.removeAttribution(attribution);
      }
    };
  }, [
    maplibreMap,
    tileHost,
    setupLayers,
    uniqueSourceName,
    polygonLayerId,
    lineLayerId,
    symbolLayerId,
    attribution,
    leafletMap,
  ]);

  // ズームレベル変化を監視
  useEffect(() => {
    if (!leafletMap) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(leafletMap.getZoom());
    };

    leafletMap.on("zoomend", handleZoomEnd);
    return () => {
      leafletMap.off("zoomend", handleZoomEnd);
    };
  }, [leafletMap]);

  // ズームレベルに応じた表示制御
  useEffect(() => {
    if (!maplibreMap) return;

    const shouldShow =
      visible &&
      zoomLevel &&
      zoomLevel - 1 >= MIN_ZOOM &&
      zoomLevel - 1 <= MAX_ZOOM;

    [polygonLayerId, lineLayerId, symbolLayerId].forEach((layerId) => {
      if (maplibreMap.getLayer(layerId)) {
        maplibreMap.setLayoutProperty(
          layerId,
          "visibility",
          shouldShow ? "visible" : "none"
        );
      }
    });

    // クリックイベントを設定する
    if (shouldShow) {
      maplibreMap.on("click", symbolLayerId, clickHandler);
    } else {
      maplibreMap.off("click", symbolLayerId, clickHandler);
    }

    return () => {
      maplibreMap.off("click", symbolLayerId, clickHandler);
    };
  }, [
    visible,
    zoomLevel,
    maplibreMap,
    polygonLayerId,
    lineLayerId,
    symbolLayerId,
    clickHandler,
  ]);

  // ポップアップのクリーンアップ
  useEffect(() => {
    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, []);

  if (!tileHost) {
    const errorMessage = "用途地域タイル: tileHost環境変数が未設定";
    console.error(errorMessage);
    Sentry.captureMessage(errorMessage);
    return null;
  }

  return null;
};
